import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useR05Api from '../api/useR05Api';
import i18n from '../assets/languages/configs';
import FujiModal from '../components/fuji-modal/FujiModal';
import { TIMEOUT_DURATION } from '../utils/Constants';
import { canDisplayNow, showNotificationDialog } from '../utils';
import useDevice from './useDevice';
import { ErrorType, OrderTicket } from 'types';

// チケットの表示ブラウザを変更する
const useChangeTicketBrowser = () => {
    const { t } = i18n;
    const [isLoading, setIsLoading] = useState(false);
    const { deviceId } = useDevice();
    const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
    const { changeTicketBrowser } = useR05Api();
    const navigate = useNavigate();

    const requestChangeDevice = useCallback(
        async (orderTicket?: OrderTicket) => {
            if (!orderTicket) {
                return;
            }
            let timeoutId: NodeJS.Timeout | undefined = undefined;
            try {
                setIsOpenConfirmationModal(false);
                const clientError = new Error() as ErrorType;
                if (!navigator.onLine) {
                    clientError.errorType = 'offline';
                    throw clientError;
                }
                setIsLoading(true);
                timeoutId = setTimeout(() => {
                    clientError.errorType = 'timeout';
                    throw clientError;
                }, TIMEOUT_DURATION);
                const response = await changeTicketBrowser({
                    deviceId: deviceId,
                    orderTicketId: orderTicket?.orderTicketId,
                    itemNumber: orderTicket?.orderUsageTickets?.[0]?.itemNumber,
                });
                console.log(response);
                showNotificationDialog(
                    'success',
                    'change-device',
                    t('notification.title'),
                    t('notification.change-browser-success'),
                );
                if (canDisplayNow(orderTicket)) {
                    navigate('/bought/ticket', {
                        state: { orderId: orderTicket.orderId, orderTicketId: orderTicket.orderTicketId },
                    });
                }
            } catch (error) {
                const showChangeDeviceError = (message: string) => {
                    showNotificationDialog('error', 'change-device', t('notification.title'), message);
                };
                const { errorType } = error as ErrorType;
                switch (errorType) {
                    case 'offline':
                    case 'timeout':
                        showChangeDeviceError(t('error-message.server-error'));
                        break;
                    case 'undefined_parameter':
                    case 'not_found_order':
                    case 'not_purchased_order':
                    case 'not_active_status':
                    case 'invalid_parameter':
                    case 'out_of_date_token':
                    case 'unknown_issuer_token':
                    case 'invalid_signature_token':
                    case 'invalid_api_key':
                        showChangeDeviceError(t('error-message.process-error'));
                        break;
                    case 'not_enough_intervals_since_activated':
                        showChangeDeviceError(t('error-message.change-browser.no-interval-since-activate-ticket'));
                        break;
                    case 'not_enough_intervals_since_device_id_active_changed':
                        showChangeDeviceError(t('error-message.change-browser.no-interval-since-change-browser'));
                        break;
                    default:
                        showChangeDeviceError(t('error-message.server-error'));
                        break;
                }
            } finally {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                }
                setIsLoading(false);
            }
        },
        [changeTicketBrowser, deviceId, navigate, t],
    );

    const ConfirmationModal = useCallback(
        ({ orderTicket }: { orderTicket?: OrderTicket }) => {
            return (
                <FujiModal
                    isOpen={isOpenConfirmationModal}
                    onCancel={() => {
                        setIsOpenConfirmationModal(false);
                    }}
                    txtCancel={t('modal.change-browser-cancel') ?? ''}
                    onSubmit={() => void requestChangeDevice(orderTicket)}
                    txtSubmit={t('modal.change-browser-ok')}
                    body={t('modal.change-browser-confirmation')}
                />
            );
        },
        [isOpenConfirmationModal, requestChangeDevice, t],
    );

    return {
        deviceId,
        isLoading,
        openConfirmationModal: useCallback(() => {
            setIsOpenConfirmationModal(true);
        }, []),
        ConfirmationModal,
    };
};

export default useChangeTicketBrowser;
